import { useCallback, useEffect, useState } from 'react';
import {
  contentintelApi,
  type APlusRecord,
  type BrandStoreRecord,
  type ContentAsset,
  type ContentDashboard,
  type ContentRecommendation,
  type ContentScore,
  type ExperimentRecord,
  type ImageIntel,
  type PerformanceMetric,
} from '../../api/contentintel';

type Tab = 'overview' | 'content' | 'aplus' | 'brand-store' | 'images' | 'performance' | 'recommendations' | 'experiments';

function scoreClass(score: number) {
  if (score >= 80) return 'text-emerald-700 bg-emerald-50';
  if (score >= 60) return 'text-amber-700 bg-amber-50';
  return 'text-red-700 bg-red-50';
}

export function ContentIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<ContentDashboard | null>(null);
  const [assets, setAssets] = useState<ContentAsset[]>([]);
  const [scores, setScores] = useState<ContentScore[]>([]);
  const [aplus, setAplus] = useState<APlusRecord[]>([]);
  const [images, setImages] = useState<ImageIntel[]>([]);
  const [brandStore, setBrandStore] = useState<BrandStoreRecord[]>([]);
  const [performance, setPerformance] = useState<PerformanceMetric[]>([]);
  const [experiments, setExperiments] = useState<ExperimentRecord[]>([]);
  const [recommendations, setRecommendations] = useState<ContentRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, cont, sc, ap, img, bs, perf, exp, recs] = await Promise.all([
        contentintelApi.dashboard(),
        contentintelApi.listContent(),
        contentintelApi.listScores(),
        contentintelApi.listAPlus(),
        contentintelApi.listImages(),
        contentintelApi.listBrandStore(),
        contentintelApi.listPerformance(),
        contentintelApi.listExperiments(),
        contentintelApi.listRecommendations(),
      ]);
      setDashboard(dash);
      setAssets(cont);
      setScores(sc);
      setAplus(ap);
      setImages(img);
      setBrandStore(bs);
      setPerformance(perf);
      setExperiments(exp);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load content intelligence data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await contentintelApi.generateRecommendations(); await refresh(); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Content Dashboard' },
    { id: 'content', label: 'Content Health' },
    { id: 'aplus', label: 'A+ Designer' },
    { id: 'brand-store', label: 'Brand Store Explorer' },
    { id: 'images', label: 'Image Performance' },
    { id: 'performance', label: 'Performance' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
    { id: 'experiments', label: 'Experiment Console' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Content Intelligence</h1>
          <p className="text-sm text-slate-500">Content optimization, brand store &amp; conversion experience — BCP-00007</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Total Assets', value: dashboard.total_assets.toString() },
              { label: 'Avg Content Score', value: `${dashboard.avg_content_score.toFixed(0)}%` },
              { label: 'Conversion Readiness', value: `${dashboard.avg_conversion_readiness.toFixed(0)}%` },
              { label: 'Low Score Assets', value: dashboard.low_score_assets.toString() },
              { label: 'A+ Coverage', value: `${dashboard.a_plus_coverage_pct.toFixed(0)}%` },
              { label: 'Brand Store Score', value: `${dashboard.brand_store_score.toFixed(0)}%` },
              { label: 'Active Experiments', value: dashboard.active_experiments.toString() },
              { label: 'Avg CTR', value: `${dashboard.avg_ctr.toFixed(1)}%` },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'content' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Completeness</th>
                <th className="px-4 py-2">SEO</th>
                <th className="px-4 py-2">Brand</th>
                <th className="px-4 py-2">Readability</th>
                <th className="px-4 py-2">Conversion Ready</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {assets.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-2 font-medium">{a.sku}</td>
                  <td className="px-4 py-2 text-slate-600">{a.title ?? '—'}</td>
                  <td className="px-4 py-2">{a.completeness_pct.toFixed(0)}%</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs ${scoreClass(a.seo_score)}`}>{a.seo_score.toFixed(0)}</span></td>
                  <td className="px-4 py-2">{a.brand_consistency.toFixed(0)}</td>
                  <td className="px-4 py-2">{a.readability.toFixed(0)}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs ${scoreClass(a.conversion_readiness)}`}>{a.conversion_readiness.toFixed(0)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {scores.length > 0 && (
            <div className="border-t border-slate-100 p-4">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Detailed Quality Scores</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {scores.map((s) => (
                  <div key={s.id} className="rounded-lg border border-slate-100 p-3 text-xs">
                    <p className="font-medium text-slate-900">{s.sku} — Overall {s.overall_score.toFixed(0)}</p>
                    <p className="text-slate-500 mt-1">SEO {s.seo_quality.toFixed(0)} · Visual {s.visual_consistency.toFixed(0)} · Clarity {s.customer_clarity.toFixed(0)} · Compliance {s.compliance.toFixed(0)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'aplus' && (
        <div className="space-y-4">
          {aplus.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <h3 className="font-medium text-slate-900">{a.sku}</h3>
                  <p className="text-sm text-slate-500">{a.module_count} modules · Layout {a.layout_score.toFixed(0)} · Visual story {a.visual_story_score.toFixed(0)}</p>
                </div>
                <div className="flex gap-2 text-xs">
                  {a.comparison_present && <span className="rounded bg-emerald-50 text-emerald-700 px-2 py-0.5">Comparison</span>}
                  {a.lifestyle_present && <span className="rounded bg-emerald-50 text-emerald-700 px-2 py-0.5">Lifestyle</span>}
                  {!a.comparison_present && <span className="rounded bg-amber-50 text-amber-700 px-2 py-0.5">Missing comparison</span>}
                  {!a.lifestyle_present && <span className="rounded bg-amber-50 text-amber-700 px-2 py-0.5">Missing lifestyle</span>}
                </div>
              </div>
              {a.recommended_modules && a.recommended_modules.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  <span className="text-xs text-slate-500 mr-1">Recommended:</span>
                  {a.recommended_modules.map((m) => (
                    <span key={m} className="rounded bg-sky-50 px-2 py-0.5 text-xs text-sky-700 capitalize">{m.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'brand-store' && (
        <div className="space-y-4">
          {brandStore.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <h3 className="font-medium text-slate-900">{b.store_name}</h3>
                <span className={`rounded px-2 py-0.5 text-sm font-medium ${scoreClass(b.overall_score)}`}>{b.overall_score.toFixed(0)} overall</span>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                {[
                  { label: 'Navigation', value: b.navigation_score },
                  { label: 'Collections', value: b.collection_score },
                  { label: 'Landing', value: b.landing_score },
                  { label: 'Cross-sell', value: b.cross_sell_score },
                  { label: 'Brand Message', value: b.brand_messaging_score },
                ].map((m) => (
                  <div key={m.label}>
                    <p className="text-slate-500 text-xs">{m.label}</p>
                    <p className="font-medium">{m.value.toFixed(0)}</p>
                  </div>
                ))}
              </div>
              {b.issues && b.issues.length > 0 && (
                <ul className="mt-3 text-xs text-amber-700 list-disc list-inside">
                  {b.issues.map((i) => <li key={i}>{i}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'images' && (
        <div className="space-y-4">
          {images.map((img) => (
            <div key={img.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-slate-900">{img.sku}</h3>
                  <p className="text-sm text-slate-500">{img.image_count} images</p>
                </div>
                <div className="flex gap-3 text-sm">
                  <span>Hero <strong>{img.hero_score.toFixed(0)}</strong></span>
                  <span>Lifestyle <strong>{img.lifestyle_score.toFixed(0)}</strong></span>
                  <span>Infographic <strong>{img.infographic_score.toFixed(0)}</strong></span>
                </div>
              </div>
              {img.recommended_order && img.recommended_order.length > 0 && (
                <p className="mt-2 text-xs text-slate-600">Recommended order: {img.recommended_order.join(' → ')}</p>
              )}
              {img.issues && img.issues.length > 0 && (
                <ul className="mt-2 text-xs text-red-600 list-disc list-inside">
                  {img.issues.map((i) => <li key={i}>{i}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'performance' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">CTR</th>
                <th className="px-4 py-2">Conversion</th>
                <th className="px-4 py-2">Engagement</th>
                <th className="px-4 py-2">Business Impact</th>
                <th className="px-4 py-2">Period</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {performance.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-2 font-medium">{p.sku}</td>
                  <td className="px-4 py-2">{(p.ctr * 100).toFixed(1)}%</td>
                  <td className="px-4 py-2">{(p.conversion_rate * 100).toFixed(1)}%</td>
                  <td className="px-4 py-2">{p.engagement_score.toFixed(0)}</td>
                  <td className="px-4 py-2">{p.business_impact_score.toFixed(0)}</td>
                  <td className="px-4 py-2 text-slate-500">{p.period ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              Generate Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <span className="text-xs font-medium uppercase text-sky-600">#{r.priority_rank} · {r.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900 mt-1">{r.title}</h3>
                  {r.description && <p className="text-sm text-slate-600 mt-1">{r.description}</p>}
                  {r.evidence && <p className="text-xs text-slate-400 mt-1">{r.evidence}</p>}
                  {r.expected_impact && <p className="text-xs text-emerald-600 mt-1">Expected: {r.expected_impact}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold">{(r.confidence_score * 100).toFixed(0)}% confidence</p>
                  {r.requires_approval && (
                    <button type="button" onClick={() => contentintelApi.approveRecommendation(r.id).then(refresh)}
                      className="mt-2 rounded bg-sky-100 px-3 py-1 text-xs font-medium text-sky-700 hover:bg-sky-200">
                      Approve
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'experiments' && (
        <div className="space-y-4">
          {experiments.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-slate-900">{e.name}</h3>
                  <p className="text-sm text-slate-500 capitalize">{e.type.replace(/_/g, ' ')} · {e.sku ?? 'Store-wide'}</p>
                </div>
                <span className={`rounded px-2 py-0.5 text-xs capitalize ${e.status === 'running' ? 'bg-sky-50 text-sky-700' : e.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-600'}`}>
                  {e.status}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div><p className="text-xs text-slate-400">Variant A</p><p>{e.variant_a ?? '—'}</p></div>
                <div><p className="text-xs text-slate-400">Variant B</p><p>{e.variant_b ?? '—'}</p></div>
                {e.ctr_impact != null && <div><p className="text-xs text-slate-400">CTR Impact</p><p className="text-emerald-600">+{e.ctr_impact.toFixed(1)}%</p></div>}
                {e.winner && <div><p className="text-xs text-slate-400">Winner</p><p className="font-medium capitalize">{e.winner.replace(/_/g, ' ')}</p></div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
